export type EraCueLevel =
  | 'deep-past'
  | 'antiquity'
  | 'medieval'
  | 'early-modern'
  | 'modern'
  | 'present'
  | 'near-future'
  | 'far-future';

export function eraLevelForYear(year: number): EraCueLevel {
  if (year <= -500) {
    return 'deep-past';
  }
  if (year <= 500) {
    return 'antiquity';
  }
  if (year <= 1500) {
    return 'medieval';
  }
  if (year <= 1800) {
    return 'early-modern';
  }
  if (year <= 2000) {
    return 'modern';
  }
  if (year <= 2030) {
    return 'present';
  }
  if (year <= 2150) {
    return 'near-future';
  }
  return 'far-future';
}

export function eraCue(level: EraCueLevel): string {
  switch (level) {
    case 'deep-past':
      return 'pre-recorded antiquity, megalithic structures, untouched landscapes, no metalwork yet, period-correct flora and fauna';
    case 'antiquity':
      return 'classical antiquity period-correct dress, hand-built stone and timber structures, dirt or paved-stone streets, no industrial signage, period-correct vehicles (carts, ships)';
    case 'medieval':
      return 'medieval period-correct clothing and architecture, timbered or stone buildings, market stalls, mud or cobblestone streets, no electricity, no internal-combustion engines';
    case 'early-modern':
      return 'early-modern period-correct clothing, masonry and brick buildings, gas lanterns or oil lamps, horse-drawn carriages, no automobiles, no electric lighting';
    case 'modern':
      return '19th-to-20th-century period-correct clothing and architecture, period-correct vehicles, no smartphones, no LED screens';
    case 'present':
      return 'contemporary period-correct clothing and architecture, modern vehicles, smartphones, present-day storefronts and signage';
    case 'near-future':
      return 'plausible near-future architecture and vehicles, soft sci-fi cues, evolved but recognizable streetscape, restrained tech overlays, no overt fantasy elements';
    case 'far-future':
      return 'speculative far-future architecture and vehicles, dramatic but plausible urban evolution, advanced materials, glowing surfaces or holographic signage permitted, no overt sci-fi tropes';
  }
}

export function yearToHumanLabel(year: number): string {
  if (year < 0) {
    return `${Math.abs(year)} BC`;
  }
  return `${year} AD`;
}

export type AnchoredPromptParams = {
  location: string;
  year: number;
  heading?: string;
  extraDirections?: string;
};

export function buildAnchoredPrompt(p: AnchoredPromptParams): string {
  const heading = p.heading?.trim() || 'north';
  const label = yearToHumanLabel(p.year);
  const cue = eraCue(eraLevelForYear(p.year));
  const lines = [
    'Create a photorealistic panorama anchored to one fixed camera position so every frame in the journey matches the same viewpoint.',
    `Location anchor: ${p.location}`,
    `Camera heading: ${heading}`,
    'Spatial anchor: identical camera placement, identical horizon height, identical eye level, identical lens position, identical field coverage, identical framing intent, identical left-right seam alignment across every year for this same location.',
    'Preserve the same plaza, street grid, terrain, coastline, monument alignment, and viewpoint geometry unless a change is historically required by the target year.',
    `Target year: ${label}`,
    `Era cue: ${cue}`,
    p.extraDirections?.trim() ? `Additional directions: ${p.extraDirections.trim()}` : null,
    'Output requirements: 360 equirectangular panoramic photograph, 2:1 aspect ratio, fully spherical, seamless at left/right edges, natural horizon centered, no fisheye distortion, no text, no watermarks, no tripod shadows, no fisheye lens edges.',
  ];

  return lines.filter((line): line is string => Boolean(line)).join('\n');
}
